import { useEffect } from 'react';
import { Page, Layout, Card, Text, BlockStack, InlineGrid, DataTable, Banner, Button, Box } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { Endpoints } from '../api/endpoints';
import ProfitChart, { ProfitPoint } from '../components/ProfitChart';
import ForecastWidget from '../components/ForecastWidget';

interface PeriodSummary {
  orders: number;
  revenue: number;
  cogs: number;
  gross_profit: number;
  net_profit: number;
  gross_margin: number;
  net_margin: number;
}

interface SummaryResponse {
  today: PeriodSummary;
  last_7d: PeriodSummary;
  last_30d: PeriodSummary;
  all_time: PeriodSummary;
}

function SummaryCard({ title, data, currency }: { title: string; data?: PeriodSummary; currency: string }) {
  const { formatMoney, formatPercent } = useCurrency(currency);
  return (
    <Card>
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm" tone="subdued">
          {title}
        </Text>
        <Text as="p" variant="headingLg">
          {formatMoney(data?.net_profit ?? 0)}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          Revenue {formatMoney(data?.revenue ?? 0)} · Net margin{' '}
          {formatPercent(data?.net_margin ?? 0)}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {data?.orders ?? 0} orders
        </Text>
      </BlockStack>
    </Card>
  );
}

interface OnboardingStatus {
  completed: number;
  total_steps: number;
  progress: number;
  dismissed: boolean;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { shop } = useShop();
  const currency = shop?.currency ?? 'USD';
  const { formatMoney } = useCurrency(currency);

  const onboarding = useApi<OnboardingStatus>(Endpoints.onboarding.status);
  const showOnboarding =
    onboarding.data &&
    !onboarding.data.dismissed &&
    onboarding.data.completed < onboarding.data.total_steps;

  // Auto-launch onboarding on first load for a not-yet-set-up shop (once per
  // browser session — afterwards the banner below reminds without hijacking).
  useEffect(() => {
    if (showOnboarding && !sessionStorage.getItem('pv_onboarding_seen')) {
      sessionStorage.setItem('pv_onboarding_seen', '1');
      navigate('/onboarding', { replace: true });
    }
  }, [showOnboarding, navigate]);

  const summary = useApi<SummaryResponse>(Endpoints.dashboard.summary);
  const chart = useApi<{ data: ProfitPoint[] }>(Endpoints.dashboard.profitChart, { range: 30 });
  const recent = useApi<{ data: RecentOrder[] }>(Endpoints.dashboard.recentOrders, { limit: 10 });
  const usage = useApi<{ plan_tier: string; orders: { used: number; limit: number | null } }>(
    Endpoints.billing.usage,
  );

  // Past the monthly order cap, new orders are skipped (ProcessOrderJob / SyncOrdersJob), so the
  // figures below stop including them. Say so instead of letting profit look silently low.
  const orderCap = usage.data?.orders.limit ?? null;
  const ordersUsed = usage.data?.orders.used ?? 0;
  const capReached = orderCap !== null && ordersUsed >= orderCap;
  const capNear = orderCap !== null && !capReached && ordersUsed >= orderCap * 0.8;

  // All-time orders is the honest test: a shop with a quiet week still has history, so only a
  // zero here means nothing was ever loaded. Wait for the fetch before judging.
  const noOrdersYet = !summary.loading && summary.data != null && summary.data.all_time.orders === 0;

  const recentRows =
    recent.data?.data?.map((o) => [
      o.order_name ?? String(o.shopify_order_id),
      new Date(o.order_date).toLocaleDateString(),
      formatMoney(o.total_revenue),
      formatMoney(o.total_cogs),
      formatMoney(o.net_profit),
    ]) ?? [];

  return (
    <Page
      title="Dashboard"
      subtitle="Real profit after all costs"
      secondaryActions={[{ content: 'Guide', onAction: () => window.open('/guide', '_blank') }]}
    >
      <Layout>
        {/* Zeros everywhere read as "you made no profit", which is a very different message from
            "we have not loaded your orders". The sync is queued at install, but it can be
            delayed, fail, or predate this shop — so say so and offer the way out. */}
        {noOrdersYet && (
          <Layout.Section>
            <Banner title="No orders loaded yet" tone="warning">
              <p>
                The figures below are zero because ProfitVault has not read any of your orders —
                not because you made no profit. Syncing pulls the last 60 days from Shopify.
              </p>
              <Box paddingBlockStart="200">
                <Button variant="primary" onClick={() => navigate('/orders')}>
                  Go to Orders to sync
                </Button>
              </Box>
            </Banner>
          </Layout.Section>
        )}
        {(capReached || capNear) && (
          <Layout.Section>
            <Banner
              title={
                capReached
                  ? `You've reached ${orderCap} orders this month on the ${usage.data!.plan_tier} plan`
                  : `${ordersUsed} of ${orderCap} orders used this month`
              }
              tone={capReached ? 'critical' : 'warning'}
            >
              <p>
                {capReached
                  ? 'New orders are not tracked until next month, so the profit below leaves them out. Upgrade to keep every order counted.'
                  : `After ${orderCap} orders, new orders stop being tracked until next month.`}
              </p>
              <Box paddingBlockStart="200">
                <Button variant="primary" onClick={() => navigate('/billing')}>
                  See plans
                </Button>
              </Box>
            </Banner>
          </Layout.Section>
        )}
        {showOnboarding && (
          <Layout.Section>
            <Banner
              title={`Finish setting up ProfitVault — ${onboarding.data!.progress}% done`}
              tone="info"
              onDismiss={() => navigate('/onboarding')}
            >
              <p>Complete the quick setup to start tracking real profit.</p>
              <Box paddingBlockStart="200">
                <Button variant="primary" onClick={() => navigate('/onboarding')}>
                  Continue setup
                </Button>
              </Box>
            </Banner>
          </Layout.Section>
        )}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="400">
            <SummaryCard title="Today" data={summary.data?.today} currency={currency} />
            <SummaryCard title="Last 7 days" data={summary.data?.last_7d} currency={currency} />
            <SummaryCard title="Last 30 days" data={summary.data?.last_30d} currency={currency} />
            <SummaryCard title="All time" data={summary.data?.all_time} currency={currency} />
          </InlineGrid>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Profit trend (30 days)
              </Text>
              <ProfitChart data={chart.data?.data ?? []} />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <ForecastWidget />
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingMd">
                Recent orders
              </Text>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
                headings={['Order', 'Date', 'Revenue', 'COGS', 'Net profit']}
                rows={recentRows}
              />
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

interface RecentOrder {
  shopify_order_id: number;
  order_name: string | null;
  order_date: string;
  total_revenue: number;
  total_cogs: number;
  net_profit: number;
}
